<template>
  <div 
    class="login-container"
    :class="{ 'opacity-0': !backgroundLoaded }"
    :style="backgroundStyle"
  >
    <div class="login-form-container">
      <div class="max-w-md w-full space-y-8 bg-white/95 dark:bg-gray-800/90 backdrop-blur-sm p-8 rounded-lg shadow-lg transform transition-all duration-300 hover:shadow-2xl">
        <div>
          <h2 class="mt-6 text-center text-3xl font-extrabold text-gray-800 dark:text-white">
            KC Portfolio Manager
          </h2>
          <p class="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
            Connexion à votre compte
          </p>
        </div>
        <form class="mt-8 space-y-6" @submit.prevent="handleSubmit">
          <div class="space-y-4">
            <div>
              <label for="email-address" class="sr-only">Adresse email</label>
              <input id="email-address" 
                     name="email" 
                     type="email" 
                     autocomplete="email" 
                     required 
                     v-model="email"
                     class="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 bg-white dark:bg-gray-700 placeholder-gray-500 text-gray-900 dark:text-white dark:border-gray-600 dark:placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm" 
                     placeholder="Adresse email">
            </div>
            <div>
              <label for="password" class="sr-only">Mot de passe</label>
              <input id="password" 
                     name="password" 
                     type="password" 
                     autocomplete="current-password" 
                     required 
                     v-model="password"
                     class="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 bg-white dark:bg-gray-700 placeholder-gray-500 text-gray-900 dark:text-white dark:border-gray-600 dark:placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm" 
                     placeholder="Mot de passe">
            </div>
          </div>

          <div v-if="error" class="bg-red-50 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
            <span class="block sm:inline">{{ error }}</span>
          </div>

          <div>
            <button type="submit" 
                    :disabled="isLoading"
                    class="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50">
              <span v-if="isLoading" class="absolute left-0 inset-y-0 flex items-center pl-3">
                <svg class="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </span>
              {{ isLoading ? 'Connexion en cours...' : 'Se connecter' }}
            </button>
          </div>

          <div class="text-sm text-center">
            <router-link to="/forgot-password" class="font-medium text-indigo-600 hover:text-indigo-500">
              Mot de passe oublié ?
            </router-link>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useRoute, useRouter } from 'vue-router'

export default {
  name: 'LoginForm',
  setup() {
    const authStore = useAuthStore()
    const router = useRouter()
    const route = useRoute()
    
    const email = ref('')
    const password = ref('')
    const error = ref('')
    const isLoading = ref(false)
    const backgroundLoaded = ref(false)
    const backgroundStyle = ref({
      backgroundImage: '',
      backgroundColor: '#f3f4f6'
    })

    onMounted(async () => {
      try {
        const { default: bgImage } = await import('@/assets/backgroundImage.js')
        backgroundStyle.value.backgroundImage = `url(${bgImage})`
        backgroundLoaded.value = true
      } catch (err) {
        console.error('Erreur lors du chargement de l\'image:', err)
        backgroundLoaded.value = true
      }
    })

    const handleSubmit = async () => {
      try {
        error.value = ''
        isLoading.value = true
        
        await authStore.login(email.value, password.value)
        
        // Rediriger vers la page demandée ou le dashboard
        const redirectPath = route.query.redirect || '/dashboard'
        router.push(redirectPath)
      } catch (err) {
        console.error('Login error:', err)
        // Amélioration de la gestion des erreurs pour afficher le message correct
        if (err.message === 'Réponse invalide du serveur') {
          error.value = 'Le serveur a renvoyé une réponse invalide. Veuillez réessayer plus tard.'
        } else {
          error.value = err.response?.data?.error || err.response?.data?.message || err.message || 'Une erreur est survenue lors de la connexion'
        }
      } finally {
        isLoading.value = false
      }
    }

    return {
      email,
      password,
      error,
      isLoading,
      handleSubmit,
      backgroundLoaded,
      backgroundStyle
    }
  }
}
</script>

<style>
.login-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background-size: cover;
  background-position: center;
  transition: opacity 0.5s ease-in-out;
  padding: 1rem;
}

.login-form-container {
  width: 100%;
  max-width: 28rem;
  margin: 0 auto;
}
</style>

